import { thunkProgress } from 'redux-progress';
import type { Dispatch, GetState } from 'model/State';
import {
  CUSTOMER_ACCOUNT_OVERVIEWS_ASYNC,
  DETAILS,
  IS_EVALUATION_ALLOWED,
  IS_RENEWABLE_ACCOUNT,
  IS_UPGRADABLE_ACCOUNT,
  ADD_CONTACT,
  MAKE_CONTACT_PRIMARY,
  VALIDATE_CONTACT,
  END_USER_CSV_DOWNLOAD_RESULT,
  MAKE_CONTACT_PRIMARY_NOTIFICATION,
  END_USER_CSV_DOWNLOAD_IN_PROGRESS,
  END_USER_CSV_DOWNLOAD_IS_MIGRATION_IN_PROGRESS,
  GET_APP_PARENT_SEN,
  POST_APP_PARENT_SEN,
  CLEAR_APP_PARENT_SEN_ERROR,
} from './types';
import {
  detail,
  getAsyncCustomerAccountOverviewsByPartnerSearchDTO,
  isEvaluationAllowed,
  isRenewableAccount,
  isUpgradableAccount,
  addContact,
  sendPromotionNotificationEmail,
  validate,
  addMainContact,
  getDownloadCsvForEndUserLicenses,
  getAppParentSen,
  postAppParentSen,
} from '../../api';
import { CustomerAccountOverviewSearchDTO } from '../../models/accounts';

export const fetchDetails =
  (accountId: string, ufoName: string = '') =>
  (dispatch: Dispatch, getState: GetState) => {
    if (!accountId) return;

    const detailsData = detail(accountId, getState(), ufoName);
    dispatch(thunkProgress(DETAILS, detailsData, { accountId }));
  };

export const fetchIsUpgradableAccount =
  (accountId: number, ufoName: string = '') =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(
      thunkProgress(IS_UPGRADABLE_ACCOUNT, isUpgradableAccount(accountId, getState(), ufoName), {
        accountId,
      }),
    );
  };

export const fetchIsRenewableAccount =
  (accountId: number, ufoName: string = '') =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(
      thunkProgress(IS_RENEWABLE_ACCOUNT, isRenewableAccount(accountId, getState(), ufoName), {
        accountId,
      }),
    );
  };
export const fetchIsEvaluationAllowed =
  (username: string, productKey: string) => (dispatch: Dispatch, getState: GetState) => {
    dispatch(
      thunkProgress(IS_EVALUATION_ALLOWED, isEvaluationAllowed(username, productKey, getState())),
    );
  };

export const fetchGetAsyncCustomerAccountOverviews =
  (searchDTO: CustomerAccountOverviewSearchDTO) => (dispatch: Dispatch, getState: GetState) => {
    dispatch(
      thunkProgress(
        CUSTOMER_ACCOUNT_OVERVIEWS_ASYNC,
        getAsyncCustomerAccountOverviewsByPartnerSearchDTO(searchDTO, getState()),
      ),
    );
  };

export const fetchDownloadCsvForEndUserLicenses =
  (pollingUuid: string, isMigration: boolean, partnerId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (isMigration) {
      dispatch({
        type: END_USER_CSV_DOWNLOAD_IS_MIGRATION_IN_PROGRESS,
        endUserCSVDownloadIsMigrationInProgress: true,
      });
    } else {
      dispatch({ type: END_USER_CSV_DOWNLOAD_IN_PROGRESS, endUserCSVDownloadInProgress: true });
    }
    dispatch(
      thunkProgress(
        END_USER_CSV_DOWNLOAD_RESULT,
        getDownloadCsvForEndUserLicenses(pollingUuid, isMigration, partnerId, getState(), dispatch),
      ),
    );
  };

export const addNewContactToAccount =
  (accountId: string, contactId: string, contactRelationshipType: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (!accountId) return;

    const addNewContact = addContact(accountId, contactId, contactRelationshipType, state);
    dispatch(thunkProgress(ADD_CONTACT, addNewContact));
  };

export const addContactAsMain =
  (accountId: string, contactId: string, contactRelationshipType: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (!accountId || !contactId) return;

    const addContactCall = addMainContact(accountId, contactId, contactRelationshipType, state);
    dispatch(thunkProgress(MAKE_CONTACT_PRIMARY, addContactCall));
  };

export const promotionNotificationEmail =
  (accountId: string, contactId: string, contactRelationshipType: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (!accountId || !contactId) return;

    const sendPromotionNotificationEmailCall = sendPromotionNotificationEmail(
      accountId,
      contactId,
      contactRelationshipType,
      state,
    );
    dispatch(thunkProgress(MAKE_CONTACT_PRIMARY_NOTIFICATION, sendPromotionNotificationEmailCall));
  };

export const validateContact =
  (accountId: string, contactId: string, contactRelationshipType: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (!accountId || !contactId) return;

    const validateContactCall = validate(accountId, contactId, contactRelationshipType, state);
    dispatch(thunkProgress(VALIDATE_CONTACT, validateContactCall));
  };

export const fetchAppParentSen =
  (appAccountId: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    dispatch(
      thunkProgress(GET_APP_PARENT_SEN, getAppParentSen(appAccountId, state), {
        accountId: appAccountId,
      }),
    );
  };

export const updateAppParentSen =
  (appAccountId: string, parentAccountId: string, parentAccountSlug: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    dispatch(
      thunkProgress(
        POST_APP_PARENT_SEN,
        postAppParentSen(appAccountId, parentAccountId, parentAccountSlug, state),
        {
          accountId: appAccountId,
          parentAccountId,
          parentAccountSlug,
        },
      ),
    );
  };

export const clearAppParentSenError = () => (dispatch: Dispatch) => {
  dispatch({ type: CLEAR_APP_PARENT_SEN_ERROR });
};
